'use client';

import { createContext, forwardRef, useContext, type ComponentProps, type ReactNode } from 'react';
import Link from 'next/link';
import { parseTrailParams, unnamedRootCrumb } from '@/lib/trail';
import { setTapTarget, type TapTrail } from './tap-mount-store';

/**
 * The trail a row on this surface gives its target, provided by the surface
 * the rows sit on: the item page provides its own trail with itself as the
 * last crumb, which is what a sub-item's page will carry. Provided BELOW the
 * pages, with a value that does not change on mount (ui-kit.md, 2026-09-27).
 */
export const TapTrailContext = createContext<TapTrail | undefined>(undefined);

/** The trail an item link's `?from=` gives, where no read is needed to name
 *  its root (`unnamedRootCrumb`, the server's own rule) and no item was
 *  drilled through: the target of a row on a list. */
function trailOf(href: string): TapTrail | undefined {
  const url = new URL(href, 'http://x');
  if (!url.pathname.startsWith('/items/')) return undefined;
  const { from, trailIds } = parseTrailParams(Object.fromEntries(url.searchParams));
  const root = unnamedRootCrumb(from);
  return root && trailIds.length === 0 ? { root, items: [] } : undefined;
}

/** What a row tells the tap's mount about its target. */
export interface TapInfo {
  /** The row's title, drawn in the item skeleton's title seat. */
  title?: string;
  /** The target's trail. Defaults to the surface's (`TapTrailContext`), then
   *  to the root the href's `?from=` names. */
  trail?: TapTrail;
}

/**
 * A `Link` whose tap mounts the target's skeleton (docs/LOADING.md §2, tool
 * 3): `onNavigate` names the target in the tap's store before the router's
 * transition starts, so the skeleton paints by the next frame whether or not
 * the router already holds the target's shell. `onNavigate` fires for a
 * client-side navigation only, so a modified click (a new tab) mounts nothing.
 */
export const TapLink = forwardRef<
  HTMLAnchorElement,
  ComponentProps<typeof Link> & { href: string; tap?: TapInfo }
>(function TapLink({ href, tap, onNavigate, ...rest }, ref) {
  const surfaceTrail = useContext(TapTrailContext);
  return (
    <Link
      ref={ref}
      href={href}
      onNavigate={(e) => {
        onNavigate?.(e);
        setTapTarget({
          href,
          title: tap?.title,
          trail: tap?.trail ?? surfaceTrail ?? trailOf(href),
        });
      }}
      {...rest}
    />
  );
});

/** Name a target for a row that navigates without a `Link` (`router.push`
 *  from a menu): call right before the push. */
export function tapTo(href: string, tap?: TapInfo): void {
  setTapTarget({ href, title: tap?.title, trail: tap?.trail ?? trailOf(href) });
}

/** Provides the trail a row on this surface gives its target (a client
 *  boundary, so a server page can provide it). */
export function TapTrailProvider({ trail, children }: { trail: TapTrail; children: ReactNode }) {
  return <TapTrailContext.Provider value={trail}>{children}</TapTrailContext.Provider>;
}
